import { fromJS, List, Map, Set } from "immutable";
import _ from "lodash";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import {
  SCROLL_RUBBER_BAND_MAX,
  SUBWAY_CARD_MARGIN,
  SUBWAY_CARD_WIDTH,
  SUBWAY_DOT_SIZE,
  SUBWAY_HEADER_HEIGHT,
  SUBWAY_HEADER_PADDING_Y,
  SUBWAY_HEADER_TICK_FREQUENCY_NO_TEXT,
  SUBWAY_HEADER_TICK_FREQUENCY_NORMAL,
  SUBWAY_NO_TEXT_SPACING,
  SUBWAY_TEXT_PADDING,
  SUBWAY_THREAD_MARGIN,
  SUBWAY_VIEW_THREAD_LABEL_LEFT,
  SUBWAY_VIEW_THREAD_LABEL_MARGIN,
  SUBWAY_VIEW_THREAD_LABEL_WIDTH,
  SUBWAY_VIEW_THREAD_THICKNESS
} from "../../constants";
import CheckboxStates from "../../constants/checkboxStates";
import { SUBWAY_VIEW } from "../../constants/identifiers";
import lineStyles from "../../constants/lineStyles";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import subwayDisplayOptions from "../../constants/subwayDisplayOptions";
import subwayTextLineCountOptions from "../../constants/subwayTextLineCountOptions";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { uiStateSelectors } from "../../modules/uiState";
import { immutableRecords } from "../../types";
import { listToIndexMap } from "../../utils/immutableExtensions";
import preprocessorQuery from "../../utils/preprocessorQuery";
import workViewSelectors from "../workView/selectors";

const nodeSetForThread = (threadItem, nodeIdSet, relationshipsById) => {
  const refIds = threadItem.references.map(refId => relationshipsById.get(refId).object);
  const invIds = threadItem.inverseReferences.map(refId => relationshipsById.get(refId).subject);
  const matchingIds = Set(refIds).union(invIds);
  return matchingIds.filter(id => nodeIdSet.contains(id));
};

const countCommonNodes = (set1, set2) => set1.reduce((c, id) => c + (set2.contains(id) ? 1 : 0), 0);

const getThreadSimilaritiesAndWeights = nodeSetsForThreads => {
  let similarities = {};
  let centerWeights = {};
  nodeSetsForThreads.forEach((nodes1, id1) => {
    similarities[id1] = {};
    centerWeights[id1] = 0;
    nodeSetsForThreads.forEach((nodes2, id2) => {
      // if we have already calculated the inverse, just use it
      if (_.has(similarities[id2], id1)) {
        similarities[id1][id2] = similarities[id2][id1];
        centerWeights[id1] += similarities[id2][id1];
      } else {
        // Otherwise, calculate it - but avoid calling intersect on identical lists
        const matchCount = id1 === id2 ? nodes1.size : countCommonNodes(nodes1, nodes2);
        similarities[id1][id2] = matchCount;
        centerWeights[id1] += id1 === id2 ? 0 : matchCount;
      }
    });
  });
  return { similarities, centerWeights };
};

const getNodesForThreads = (threadItems, itemIds, nodeSetForThread) => {
  // First, get the node sets for every thread
  const nodes = {};
  // populate with empty array
  threadItems.forEach(t => {
    nodes[t.id] = [];
  });

  itemIds.forEach(id => {
    threadItems.forEach(t => {
      if (nodeSetForThread.get(t.id).contains(id)) {
        nodes[t.id].push(id);
      }
    });
  });
  return fromJS(nodes);
};

const getNodeSetsForThreads = (threadItems, nodeIdSet, relationshipsById) => {
  // First, get the node sets for every thread
  const nodes = {};
  threadItems.forEach(t => {
    nodes[t.id] = nodeSetForThread(t, nodeIdSet, relationshipsById);
  });
  return Map(nodes);
};

const getThreadsForNode = (nodeSetsForThreads, itemId) => nodeSetsForThreads.filter(nodes => nodes.contains(itemId));

const scoreThreadOrder = (threadIds, similarities, _centerWeights) => {
  let score = 0;
  const n = threadIds.length;
  // const middle = Math.floor(n / 2);

  for (let i = 0; i < n; i++) {
    const id1 = threadIds[i];
    // score += centerWeights[id1] * Math.abs(middle - i);
    for (let j = i + 1; j < n; j++) {
      const id2 = threadIds[j];
      score += similarities[id1][id2] * (j - i);
    }
  }
  return score;
};

const findBestThreadOrder = (threadIds, similarities, centerWeights) => {
  if (threadIds.length <= 2) {
    return threadIds;
  }

  // Work out the order to insert them, so that the most connected nodes are added first and the latter are
  // added to the periphery
  let insertOrder = _.clone(threadIds);
  insertOrder.sort((a, b) => centerWeights[b] - centerWeights[a]);

  let result = [insertOrder[0]];

  const length = insertOrder.length;

  // For each
  for (let i = 1; i < length; i++) {
    let bestScore = null;
    let bestOrder = null;
    for (let j = 0; j <= result.length; j++) {
      let test = _.clone(result);
      test.splice(j, 0, insertOrder[i]);
      const myScore = scoreThreadOrder(test, similarities, centerWeights);
      if (!bestScore || myScore < bestScore) {
        bestScore = myScore;
        bestOrder = test;
      }
    }
    result = bestOrder;
  }

  return result;
};

const getTicksForNodes = (filteredSequenceIds, displayItems, numTextLines, getSubwayXForIndex) => {
  const tickFrequency =
    numTextLines === subwayTextLineCountOptions.NO_LINES
      ? SUBWAY_HEADER_TICK_FREQUENCY_NO_TEXT
      : SUBWAY_HEADER_TICK_FREQUENCY_NORMAL;

  let nextIndex = 0;
  const result = filteredSequenceIds
    .map((id, index) => {
      const obj = displayItems.get(id);
      const dateText = obj && obj.startDate ? obj.startDate.label : null;
      if (dateText && index >= nextIndex) {
        nextIndex = index + tickFrequency;
        return immutableRecords.SubwayTickDisplayRecord({
          x: getSubwayXForIndex(index),
          label: dateText
        });
      }
      return null;
    })
    .filter(tick => tick);

  return result;
};

const getSequenceSubwayData = (
  itemsById,
  typesById,
  filteredSequenceIds,
  threadNodeSets,
  displayItems,
  relationshipsById,
  colorMap,
  subwayViewSettings,
  subwayViewOptions,
  fontSize
) => {
  // First work out the order we want our threads to be...
  const threadItems = subwayViewOptions.threadIds.map(id => itemsById.get(id));

  // Now work out our nodes for each thread
  let threadNodes = getNodesForThreads(threadItems, filteredSequenceIds, threadNodeSets);
  let finalThreadIds = subwayViewOptions.threadIds;

  const { similarities, centerWeights } = getThreadSimilaritiesAndWeights(threadNodeSets);
  // Sort the threads by order of weight (for node allocation purposes)
  let threadsByWeight = _.map(centerWeights, (weight, id) => {
    return { weight, id };
  });

  threadsByWeight.sort((a, b) => b.weight - a.weight);
  threadsByWeight = List(_.map(threadsByWeight, obj => obj.id));

  // Sort the threads by similarity/centrality for display purposes
  const threadOrder = List(findBestThreadOrder(finalThreadIds.toArray(), similarities, centerWeights));

  const SCALED_THREAD_LABEL_WIDTH = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_WIDTH, fontSize);
  const SCALED_THREAD_LABEL_MARGIN = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_MARGIN, fontSize);
  const SCALED_CARD_WIDTH = scalePixelsToFontSize(SUBWAY_CARD_WIDTH, fontSize);
  const SCALED_CARD_MARGIN = scalePixelsToFontSize(SUBWAY_CARD_MARGIN, fontSize);
  const SCALED_THREAD_MARGIN = scalePixelsToFontSize(SUBWAY_THREAD_MARGIN, fontSize);
  const SCALED_HEADER_HEIGHT =
    Math.ceil(scalePixelsToFontSize(SUBWAY_HEADER_HEIGHT, fontSize)) + SUBWAY_HEADER_PADDING_Y;
  const SCALED_DOT_SIZE = scalePixelsToFontSize(SUBWAY_DOT_SIZE, fontSize);
  const SCALED_THREAD_LABEL_LEFT = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_LEFT, fontSize);
  const SCALED_TEXT_PADDING = scalePixelsToFontSize(SUBWAY_TEXT_PADDING, fontSize);
  const SCALED_THREAD_THICKNESS = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_THICKNESS, fontSize);

  let totalCardWidth = null;

  if (subwayViewSettings.numTextLines === subwayTextLineCountOptions.NO_LINES) {
    const SCALED_NO_TEXT_SPACING = scalePixelsToFontSize(SUBWAY_NO_TEXT_SPACING, fontSize);
    totalCardWidth = SCALED_NO_TEXT_SPACING;
  } else {
    totalCardWidth = SCALED_CARD_WIDTH / 2 + SCALED_CARD_MARGIN * 2;
  }

  const width =
    SCALED_THREAD_LABEL_WIDTH + SCALED_THREAD_LABEL_MARGIN + filteredSequenceIds.size * totalCardWidth + totalCardWidth;

  const getSubwayXForIndex = index => {
    return SCALED_THREAD_LABEL_MARGIN + SCALED_CARD_MARGIN + Math.max(0, index) * totalCardWidth;
  };

  let top = SCALED_HEADER_HEIGHT;

  let allNodes = List();

  // Track the position of nodes within threads
  let threadForNode = {};
  let positionForNode = {};
  let nodesInRow = {};
  let indexForNode = {};

  // Pre-populate array of nodes for each thread
  threadOrder.forEach((_id, index) => (nodesInRow[index] = []));
  // Iterate through every node and allocate to a thread.
  filteredSequenceIds.forEach((itemId, index) => {
    // Work out which thread to allocate node to.
    // Should be allocated to the thread with the highest centerWeight
    // i.e. the one with the most connected nodes.
    const threadId = threadsByWeight.find(threadId => threadNodeSets.get(threadId).contains(itemId));
    const rowIndex = threadOrder.indexOf(threadId);
    // Mark which thread this node is in
    threadForNode[itemId] = threadId;
    indexForNode[itemId] = index;
    // And record which nodes are placed in which row
    nodesInRow[rowIndex].push(itemId);
  });

  // Now iterate through every thread and work out the placement of nodes, nodeText, and the subsequent height
  // and y-position of each thread.

  let threads = threadOrder.map((threadId, threadRow) => {
    const threadObj = threadId ? displayItems.get(threadId) : null;
    let label = "All Events";
    let color = "mono";
    let initials = null;
    let typeIcon = "blank";
    let typeIconColor = "iconMono";
    let typeId = null;
    let typeLabel = null;
    let typeLabelDisplay = null;
    let nodeIds = nodesInRow[threadRow];
    let heightAbove = 0;
    let heightBelow = 0;
    let prevIndex = null;
    let prevWasAbove = false;

    const preferAbove = threadRow <= threadOrder.size / 2;

    if (threadObj) {
      // Normal case, we are creating a thread for a specific item
      label = threadObj.label;
      color = itemSelectors.getItemSwatchColor(colorMap, threadObj);
      const type = typesById.get(threadObj.type);
      initials = threadObj.initials;
      typeLabelDisplay = typeSelectors.getTypeLabelDisplay(type);
      const typeData = typeSelectors.getTypeValuesFromType(type);
      typeLabel = typeData.label;
      typeIcon = typeData.icon;
      typeIconColor = typeData.color;
      typeId = threadObj.type;
    }

    let nodes = _.map(nodeIds, id => {
      // Get result
      const index = indexForNode[id];
      const x = getSubwayXForIndex(index);

      const placeAbove = prevIndex === null || index > prevIndex + 1 ? preferAbove : !prevWasAbove;
      prevIndex = index;
      prevWasAbove = placeAbove;

      const obj = displayItems.get(id);
      const type = typesById.get(obj.type);
      const typeLabelDisplay = typeSelectors.getTypeLabelDisplay(type);
      const typeData = typeSelectors.getTypeValuesFromType(type);

      const textHeight = obj.subwayTitleMetrics.size.height;
      const yTextOffset = placeAbove
        ? -SCALED_DOT_SIZE / 2 - textHeight - SCALED_TEXT_PADDING
        : SCALED_DOT_SIZE / 2 + SCALED_TEXT_PADDING;

      const titleMetrics = obj.subwayTitleMetrics;
      const titleOffset = immutableRecords.PointRecord({
        x: -SCALED_DOT_SIZE / 2,
        y: yTextOffset
      });

      const result = immutableRecords.SubwayNodeDisplayRecord({
        nodeId: `${threadId}-${id}`,
        itemId: id,
        itemType: obj.type,
        titleMetrics: titleMetrics,
        label: obj.label,
        titleOffset,
        color: obj.color,
        isCompleted: obj.isCompleted,
        icon: typeData.icon,
        iconColor: typeData.color,
        typeLabel: typeData.label,
        typeLabelDisplay: typeLabelDisplay,
        junction: getThreadsForNode(threadNodeSets, id).size > 1,
        x,
        y: top,
        threadId,
        sequenceIndex: index
      });

      // Update dimensions
      if (placeAbove && textHeight > heightAbove) {
        heightAbove = textHeight;
      }
      if (!placeAbove && textHeight > heightBelow) {
        heightBelow = textHeight;
      }
      return result;
    });

    // We now know our height above and below, so we can calculate the middle position for the line
    const middleY = top + heightAbove + SCALED_DOT_SIZE / 2;
    const height = heightAbove + SCALED_TEXT_PADDING + SCALED_DOT_SIZE + SCALED_TEXT_PADDING + heightBelow;

    // Now adjust all nodes so they lie on the middle of the line
    nodes = nodes.map(n => n.set("y", middleY));

    // And store the position of the node for when we want to work out the lines
    nodes.forEach(n => {
      positionForNode[n.itemId] = {
        x: n.x,
        y: n.y,
        row: threadRow,
        column: indexForNode[n.itemId]
      };
    });

    allNodes = allNodes.concat(nodes);

    const result = immutableRecords.SubwayThreadDisplayRecord({
      id: threadId,
      label,
      initials,
      typeId,
      typeIcon,
      typeIconColor,
      typeLabel,
      typeLabelDisplay,
      color,
      lineStyle: lineStyles.SOLID,
      anchorY: middleY,
      top: top - SCALED_THREAD_MARGIN,
      bottom: top + height + SCALED_THREAD_MARGIN
    });

    top += height + 2 * SCALED_THREAD_MARGIN;

    return result;
  });

  // In thread order, create a list of every thread that transitions from Node A to Node B.
  // This will give us a sorted list of all threads that have to follow a certain path
  let nodeTransitions = {};

  threadOrder.forEach(threadId => {
    // Get the list of nodes that belong to this thread.
    const nodeIds = threadNodes.get(threadId).toArray();
    const positions = _.map(nodeIds, id => positionForNode[id]);
    const nodeCount = nodeIds.length;

    // Work through them in order and build the nodeTransitions
    for (let n = 0; n < nodeCount - 1; n++) {
      // Find the two node IDs we are referring to...
      const a = nodeIds[n];
      const b = nodeIds[n + 1];
      const aPos = positions[n];
      const bPos = positions[n + 1];
      // Create object for a if it is indexOf
      if (!nodeTransitions[a]) {
        nodeTransitions[a] = {};
      }

      // Have we made this transition before?
      if (nodeTransitions[a][b]) {
        // If so, just add this threadId to the threads list and move on
        nodeTransitions[a][b].threads.push(threadId);
      } else {
        // Otherwise, we need to create a new nodeTransition object
        nodeTransitions[a][b] = {
          threads: [threadId],
          colSpan: bPos.column - aPos.column + 1,
          startY: aPos.y,
          stopY: bPos.y,
          path: null
        };
      }
    }
  });

  // Now that we have filled in all nodeTransitions, loop through the list created above and work out
  // the path those nodeTransitions should take
  _.each(nodeTransitions, transitionsFromA => {
    _.each(transitionsFromA, transition => {
      // we want to fill in the path for this "transition"
      // Work out the most important thread in this transition
      const bestThread = _.minBy(transition.threads, t => threadsByWeight.indexOf(t));
      // What is the threadIndex of bestThread?
      const thread = threads.find(t => t.id === bestThread);

      // Path starts at startRow
      transition.path = [transition.startY];
      // Each node in between should sit on our bestThread
      for (let i = 1; i < transition.colSpan - 1; i++) {
        transition.path.push(thread.anchorY);
      }
      // And finally, transition to the endRow
      transition.path.push(transition.stopY);
    });
  });

  // Now that we know the path to take for every transition, we can build up a list of vertices that we want
  // each thread to pass through.
  let totalAddIconX = 0;

  const calcThreadOffset = (index, count) =>
    index * SCALED_THREAD_THICKNESS - (count * SCALED_THREAD_THICKNESS) / 2 + SCALED_THREAD_THICKNESS / 2;

  const calcLineOffsets = (x1, y1, x2, y2, offsetDistance) => {
    let x = 0;
    let y = 0;
    if (y1 === y2) {
      x = 0;
      y = offsetDistance;
    } else {
      const theta = Math.PI / 2 - Math.atan(Math.abs(y2 - y1) / Math.abs(x2 - x1));
      y = Math.sin(theta) * offsetDistance;
      x = Math.cos(theta) * (y2 > y1 ? -offsetDistance : offsetDistance);
    }
    return { x, y };
  };

  threads = threads.map(t => {
    const nodeIds = threadNodes.get(t.id).toArray();
    const positions = _.map(nodeIds, id => positionForNode[id]);
    const nodeCount = nodeIds.length;

    let vertices = [];

    // The first vertex is always adjacent to the name
    const firstPos = positions[0];
    vertices.push(
      immutableRecords.PointRecord({
        x: -SCROLL_RUBBER_BAND_MAX,
        y: t.anchorY
      })
    );
    if (firstPos) {
      // Assuming there are any further nodes, the next vertex will be the column before the
      // first node.
      vertices.push(
        immutableRecords.PointRecord({
          x: getSubwayXForIndex(firstPos.column - 1),
          y: t.anchorY
        })
      );
      // And also draw a line from that position to the first node
      vertices.push(
        immutableRecords.PointRecord({
          x: getSubwayXForIndex(firstPos.column),
          y: firstPos.y
        })
      );
    }

    // Once we have the first line drawn, every vertex from here is built directly from the nodeTransitions we
    // have already constructed.

    // Go through the nodes of this thread in order
    for (let n = 0; n < nodeCount - 1; n++) {
      // Find the two node IDs we are referring to...
      const a = nodeIds[n];
      const b = nodeIds[n + 1];
      const aPos = positions[n];

      // Find the transition in question
      const transition = nodeTransitions[a][b];

      // Work out the y-offset to be applied
      const offsetDistance = calcThreadOffset(_.indexOf(transition.threads, t.id), transition.threads.length);

      // Now loop through every point and calculate the vertex it represents
      for (let i = 0; i < transition.path.length; i++) {
        // Work out the four points for this line.
        const x0 = getSubwayXForIndex(i + aPos.column - 1);
        const y0 = i === 0 ? transition.path[i] : transition.path[i - 1];
        const x1 = getSubwayXForIndex(i + aPos.column);
        const y1 = transition.path[i];
        const x2 = i === transition.path.length - 1 ? x1 : getSubwayXForIndex(i + aPos.column + 1);
        const y2 = i === transition.path.length - 1 ? y1 : transition.path[i + 1];

        // Work out the offset required to prevent parallel lines from overlapping
        const offsets1 = calcLineOffsets(x0, y0, x1, y1, offsetDistance);
        const offsets2 = calcLineOffsets(x1, y1, x2, y2, offsetDistance);

        // Work out the points to use for the line intersection equations
        // We want two lines:
        // (1) y - yA = mA (x - xA)
        // (2) y - yB = mB (x - xB)
        const mA = x1 === x0 ? 0 : (y1 - y0) / (x1 - x0);
        const mB = x2 === x1 ? 0 : (y2 - y1) / (x2 - x1);
        const xA = x0 + offsets1.x;
        const yA = y0 + offsets1.y;
        const xB = x1 + offsets2.x;
        const yB = y1 + offsets2.y;

        // Now find the intersection point...
        const x = mA === mB ? x1 : (mA * xA - yA - mB * xB + yB) / (mA - mB);
        const y = mA * (x - xA) + yA;

        // Work out the slope of the line
        vertices.push(
          immutableRecords.PointRecord({
            x,
            y
          })
        );
      }
    }

    const lastVertex = _.last(vertices);
    const lastX = Math.max(0, lastVertex ? lastVertex.x : SCALED_THREAD_LABEL_LEFT + SCALED_THREAD_LABEL_WIDTH);
    const addIconX = lastX + totalCardWidth;
    totalAddIconX = Math.max(totalAddIconX, addIconX);

    return t.merge({ vertices: List(vertices), addIconX });
  });

  const rect = immutableRecords.FlatRectRecord({
    left: 0,
    right: width,
    width,
    top: 0,
    bottom: top,
    height: top
  });

  const ticks = getTicksForNodes(
    filteredSequenceIds,
    displayItems,
    subwayViewSettings.numTextLines,
    getSubwayXForIndex
  );

  return immutableRecords.SubwayDisplayRecord({
    threads,
    nodes: allNodes,
    ticks,
    rect,
    display: subwayViewSettings.get("display"),
    totalAddIconX
  });
};

const getSplitSubwayData = (
  itemsById,
  sortedThreadIds,
  typesById,
  filteredSequenceIds,
  threadNodeSets,
  displayItems,
  relationshipsById,
  colorMap,
  subwayViewSettings,
  subwayViewOptions,
  fontSize
) => {
  const SCALED_CARD_WIDTH = scalePixelsToFontSize(SUBWAY_CARD_WIDTH, fontSize);
  const SCALED_CARD_MARGIN = scalePixelsToFontSize(SUBWAY_CARD_MARGIN, fontSize);
  const SCALED_THREAD_LABEL_WIDTH = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_WIDTH, fontSize);
  const SCALED_THREAD_LABEL_MARGIN = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_MARGIN, fontSize);
  const SCALED_HEADER_HEIGHT =
    Math.ceil(scalePixelsToFontSize(SUBWAY_HEADER_HEIGHT, fontSize)) + SUBWAY_HEADER_PADDING_Y;
  const SCALED_THREAD_MARGIN = scalePixelsToFontSize(SUBWAY_THREAD_MARGIN, fontSize);
  const SCALED_DOT_SIZE = scalePixelsToFontSize(SUBWAY_DOT_SIZE, fontSize);
  const SCALED_TEXT_PADDING = scalePixelsToFontSize(SUBWAY_TEXT_PADDING, fontSize);

  let totalCardWidth = null;

  if (subwayViewSettings.numTextLines === subwayTextLineCountOptions.NO_LINES) {
    const SCALED_NO_TEXT_SPACING = scalePixelsToFontSize(SUBWAY_NO_TEXT_SPACING, fontSize);
    totalCardWidth = SCALED_NO_TEXT_SPACING;
  } else {
    totalCardWidth = SCALED_CARD_WIDTH / 2 + SCALED_CARD_MARGIN * 2;
  }

  const width =
    SCALED_THREAD_LABEL_WIDTH + SCALED_THREAD_LABEL_MARGIN + filteredSequenceIds.size * totalCardWidth + totalCardWidth;

  let top = SCALED_HEADER_HEIGHT;

  let allNodes = List();

  const threadItems = subwayViewOptions.threadIds.map(id => itemsById.get(id));

  // Now work out our nodes for each thread
  let threadNodes = getNodesForThreads(threadItems, filteredSequenceIds, threadNodeSets);

  const filteredSequenceIndexMap = listToIndexMap(filteredSequenceIds);

  let finalThreadIds = sortedThreadIds;

  let totalAddIconX = 0;

  const getSubwayXForIndex = index => SCALED_THREAD_LABEL_MARGIN + SCALED_CARD_MARGIN + index * totalCardWidth;

  const threads = finalThreadIds.map(threadId => {
    const threadObj = threadId ? displayItems.get(threadId) : null;
    const label = threadObj ? threadObj.label : "All Events";
    const color = threadObj ? itemSelectors.getItemSwatchColor(colorMap, threadObj) : "mono";
    const initials = threadObj ? threadObj.initials : null;
    const type = typesById.get(threadObj.type);
    const typeLabelDisplay = typeSelectors.getTypeLabelDisplay(type);
    const typeData = threadObj ? typeSelectors.getTypeValuesFromType(type) : null;
    const typeId = threadObj ? threadObj.type : null;
    const typeIcon = typeData ? typeData.icon : "blank";
    const typeIconColor = typeData ? typeData.color : "iconMono";
    let filteredIds = threadNodes.get(threadId);
    let heightAbove = 0;
    let heightBelow = 0;
    let prevIndex = null;
    let prevWasAbove = false;

    let nodes = filteredIds.map(id => {
      // Get result
      const index = filteredSequenceIndexMap.get(id);
      const x = getSubwayXForIndex(index);

      const placeAbove = prevIndex === null || index > prevIndex + 1 ? true : !prevWasAbove;
      prevIndex = index;
      prevWasAbove = placeAbove;

      const obj = displayItems.get(id);
      const type = typesById.get(obj.type);
      const typeLabelDisplay = typeSelectors.getTypeLabelDisplay(type);
      const typeData = typeSelectors.getTypeValuesFromType(type);

      const textHeight = obj.subwayTitleMetrics.size.height;
      const yTextOffset = placeAbove
        ? -SCALED_DOT_SIZE / 2 - textHeight - SCALED_TEXT_PADDING
        : SCALED_DOT_SIZE / 2 + SCALED_TEXT_PADDING;

      const titleMetrics = obj.subwayTitleMetrics;
      const titleOffset = immutableRecords.PointRecord({
        x: -SCALED_DOT_SIZE / 2,
        y: yTextOffset
      });

      const result = immutableRecords.SubwayNodeDisplayRecord({
        nodeId: `${threadId}-${id}`,
        itemId: id,
        itemType: obj.type,
        titleMetrics: titleMetrics,
        label: obj.label,
        titleOffset,
        color: obj.color,
        isCompleted: obj.isCompleted,
        icon: typeData.icon,
        iconColor: typeData.color,
        typeLabel: typeData.label,
        typeLabelDisplay: typeLabelDisplay,
        junction: getThreadsForNode(threadNodes, id).size > 1,
        x,
        y: top,
        threadId,
        sequenceIndex: index
      });

      // Update dimensions
      if (placeAbove && textHeight > heightAbove) {
        heightAbove = textHeight;
      }
      if (!placeAbove && textHeight > heightBelow) {
        heightBelow = textHeight;
      }
      return result;
    });

    // We now know our height above and below, so we can calculate the middle position for the line
    const middleY = top + heightAbove + SCALED_DOT_SIZE / 2;
    const height = heightAbove + SCALED_TEXT_PADDING + SCALED_DOT_SIZE + SCALED_TEXT_PADDING + heightBelow;

    // Now adjust all nodes so they lie on the middle of the line
    nodes = nodes.map(n => n.set("y", middleY));

    allNodes = allNodes.concat(nodes);

    const lastNode = nodes.last();
    const lastNodeX = lastNode ? lastNode.x : 0;
    const addIconX = lastNodeX + totalCardWidth;
    totalAddIconX = Math.max(totalAddIconX, addIconX);

    const vertices = List([
      immutableRecords.PointRecord({
        x: -SCROLL_RUBBER_BAND_MAX,
        y: middleY
      }),
      immutableRecords.PointRecord({
        x: lastNodeX,
        y: middleY
      })
    ]);

    const result = immutableRecords.SubwayThreadDisplayRecord({
      id: threadId,
      label,
      color,
      initials,
      typeId,
      typeIcon,
      typeIconColor,
      typeLabel: typeData.label,
      typeLabelDisplay: typeLabelDisplay,
      lineStyle: lineStyles.SOLID,
      vertices,
      anchorY: middleY,
      addIconX: addIconX,
      top: top - SCALED_THREAD_MARGIN,
      bottom: top + height + SCALED_THREAD_MARGIN
    });

    top += height + 2 * SCALED_THREAD_MARGIN;

    return result;
  });

  const rect = immutableRecords.FlatRectRecord({
    left: 0,
    right: width,
    width,
    top: 0,
    bottom: top,
    height: top
  });

  const ticks = getTicksForNodes(
    filteredSequenceIds,
    displayItems,
    subwayViewSettings.numTextLines,
    getSubwayXForIndex
  );

  return immutableRecords.SubwayDisplayRecord({
    threads,
    nodes: allNodes,
    ticks,
    rect,
    display: subwayViewSettings.get("display"),
    totalAddIconX
  });
};

const getSubwayData = (
  itemsById,
  sortedThreadIds,
  typesById,
  sequenceIds,
  filteredItemIds,
  displayItems,
  relationshipsById,
  colorMap,
  subwayViewSettings,
  subwayViewOptions,
  fontSize
) => {
  const filteredItemIdSet = Set(filteredItemIds);
  const threadItems = subwayViewOptions.threadIds.map(id => itemsById.get(id));
  const filteredSequenceSet = Set(sequenceIds.filter(id => filteredItemIdSet.contains(id)));
  const threadNodeSets = getNodeSetsForThreads(threadItems, filteredSequenceSet, relationshipsById);
  const filteredSequenceIds = getVisibleSequenceIds(sequenceIds, threadNodeSets);
  const displayItemsMap = Map(displayItems.map(item => [item.id, item]));

  if (subwayViewSettings.get("display") === subwayDisplayOptions.SUBWAY) {
    return getSequenceSubwayData(
      itemsById,
      typesById,
      filteredSequenceIds,
      threadNodeSets,
      displayItemsMap,
      relationshipsById,
      colorMap,
      subwayViewSettings,
      subwayViewOptions,
      fontSize
    );
  } else {
    return getSplitSubwayData(
      itemsById,
      sortedThreadIds,
      typesById,
      filteredSequenceIds,
      threadNodeSets,
      displayItemsMap,
      relationshipsById,
      colorMap,
      subwayViewSettings,
      subwayViewOptions,
      fontSize
    );
  }
};

const getSubwaySortedThreadId = createSelector(
  itemSelectors.getItemIndexesInTreeOrderForAllTypes,
  appStateSelectors.getViewSubwayThreadIds,
  (itemIndexes, threadIds) => {
    return threadIds.sort((a, b) => itemIndexes.get(a) - itemIndexes.get(b));
  }
);

const getSubwayDataForView = createCachedSelector(
  // fetch parameters for last calculate function
  // itemsById
  state => state.getIn(["data", "items", "byId"]),
  // sorted thread ids
  getSubwaySortedThreadId,
  // typesById
  state => definitionSelectors.getTypesById(state),
  // sequenceIds
  state => state.getIn(["data", "sequence"]),
  // filteredItemIds
  workViewSelectors.getItemIdsMatchingFilterForView,
  // displayItems
  state => workViewSelectors.getDisplayItemsForViewType(state, SUBWAY_VIEW),
  // relationshipsById
  state => state.getIn(["data", "relationships", "byId"]),
  // colorMap
  state => definitionSelectors.getColorMap(state),
  // subwayViewSettings
  state => definitionSelectors.getSubwayViewSettings(state),
  // subwayViewOptions
  appStateSelectors.getSubwayOptionsForView,
  // fontSize
  appStateSelectors.getFontSize,
  // and calculate
  getSubwayData
)(workViewSelectors.getViewId);

const combineFilterAndSelectedIds = (state, viewId) => {
  const filteredIds = workViewSelectors.getItemIdsMatchingFilterForView(state, viewId);
  const selectedIds = uiStateSelectors.getSelectedItems(state);
  return selectedIds.filter(si => filteredIds.contains(si));
};

const getSubwaySelectedDataForView = createCachedSelector(
  // fetch parameters for last calculate function
  // itemsById
  state => state.getIn(["data", "items", "byId"]),
  // sorted thread ids
  getSubwaySortedThreadId,
  // typesById
  state => definitionSelectors.getTypesById(state),
  // sequenceIds
  state => state.getIn(["data", "sequence"]),
  // filteredItemIds
  combineFilterAndSelectedIds,
  // displayItems
  state => workViewSelectors.getDisplayItemsForViewType(state, SUBWAY_VIEW),
  // relationshipsById
  state => state.getIn(["data", "relationships", "byId"]),
  // colorMap
  state => definitionSelectors.getColorMap(state),
  // subwayViewSettings
  state => definitionSelectors.getSubwayViewSettings(state),
  // subwayViewOptions
  appStateSelectors.getSubwayOptionsForView,
  // fontSize
  appStateSelectors.getFontSize,
  // and calculate
  getSubwayData
)(workViewSelectors.getViewId);

const getVisibleSequenceIds = (sequenceIds, threadNodeSets) => {
  const nodeSets = threadNodeSets.flatten();
  return sequenceIds.filter(i => nodeSets.contains(i));
};

const getVisibleSubwayIdsForView = createCachedSelector(
  // fetch parameters for last calculate function
  // itemsById
  state => state.getIn(["data", "items", "byId"]),
  // sequenceIds
  state => state.getIn(["data", "sequence"]),
  // filteredItemIds
  workViewSelectors.getItemIdsMatchingFilterForView,
  // relationshipsById
  state => state.getIn(["data", "relationships", "byId"]),
  // subwayViewOptions
  appStateSelectors.getSubwayOptionsForView,
  // and calculate
  (itemsById, sequenceIds, filteredItemIds, relationshipsById, subwayViewOptions) => {
    // TODO: Later, we may want to break this crap into its own selector so it is
    // re-used here and getSubwayData()
    const filteredItemIdSet = Set(filteredItemIds);
    const threadItems = subwayViewOptions.threadIds.map(id => itemsById.get(id));
    const filteredSequenceSet = Set(sequenceIds.filter(id => filteredItemIdSet.contains(id)));
    const threadNodeSets = getNodeSetsForThreads(threadItems, filteredSequenceSet, relationshipsById);
    return getVisibleSequenceIds(sequenceIds, threadNodeSets);
  }
)(workViewSelectors.getViewId);

const getSubwayViewData = getSubwayDataForView;

const getSubwayViewSelectedData = getSubwaySelectedDataForView;

const getAllowedThreads = createSelector(
  // fetch parameters for last calculate function
  state => definitionSelectors.getEnabledDefinitions(state).types,
  state => state.data.items,
  // Calculate the final result
  (types, items) => {
    const columnTypes = types.allIds.map(tId => types.byId.get(tId)).filter(t => t.showAsColumn);
    return columnTypes.map(t => {
      const groupOptions = items.allIdsForType.get(t.id).map(oId => {
        const o = items.byId.get(oId);
        return immutableRecords.DropdownOptionRecord({
          value: o.id,
          label: o.label
        });
      });
      const allowAdd = !preprocessorQuery.isReadonly();

      return immutableRecords.DropdownGroupRecord({
        value: t.id,
        label: t.label,
        children: groupOptions,
        allowAdd,
        disabled: !groupOptions.size
      });
    });
  }
);

const getAllowedThreadsForView = createCachedSelector(
  getAllowedThreads,
  appStateSelectors.getViewSubwayThreadIds,
  (columnGroups, selectedColumns) => {
    return columnGroups.map(cg => {
      return cg.withMutations(g => {
        let foundChecked = false;
        let foundUnchecked = false;
        let state = null;
        if (g.has("children")) {
          g.update("children", children =>
            children.map(c => {
              const selected = selectedColumns.contains(c.value);
              if (selected) {
                foundChecked = true;
              } else {
                foundUnchecked = true;
              }
              return c.set("selected", selected);
            })
          );
          state =
            foundChecked && foundUnchecked
              ? CheckboxStates.MIXED
              : foundChecked
                ? CheckboxStates.CHECKED
                : CheckboxStates.UNCHECKED;
          g.set("state", state);
        }
      });
    });
  }
)(workViewSelectors.getViewId);

const getIndexForXPosition = (xPos, fontSize, movingLeft) => {
  // const SCALED_THREAD_LABEL_WIDTH = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_WIDTH, fontSize);
  const SCALED_THREAD_LABEL_MARGIN = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_MARGIN, fontSize);
  const SCALED_CARD_WIDTH = scalePixelsToFontSize(SUBWAY_CARD_WIDTH, fontSize);
  const SCALED_CARD_MARGIN = scalePixelsToFontSize(SUBWAY_CARD_MARGIN, fontSize);
  const totalCardWidth = SCALED_CARD_WIDTH / 2 + SCALED_CARD_MARGIN * 2;
  const floatingIndex = (xPos - SCALED_THREAD_LABEL_MARGIN - SCALED_CARD_MARGIN) / totalCardWidth;
  return movingLeft ? Math.ceil(floatingIndex) : Math.floor(floatingIndex);
};

const getDropPositionForIndex = (index, fontSize, movingLeft) => {
  // const SCALED_THREAD_LABEL_WIDTH = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_WIDTH, fontSize);
  const SCALED_THREAD_LABEL_MARGIN = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_MARGIN, fontSize);
  const SCALED_CARD_WIDTH = scalePixelsToFontSize(SUBWAY_CARD_WIDTH, fontSize);
  const SCALED_CARD_MARGIN = scalePixelsToFontSize(SUBWAY_CARD_MARGIN, fontSize);
  const totalCardWidth = SCALED_CARD_WIDTH / 2 + SCALED_CARD_MARGIN * 2;
  return (
    SCALED_THREAD_LABEL_MARGIN +
    SCALED_CARD_MARGIN +
    index * totalCardWidth +
    (movingLeft ? -totalCardWidth / 2 : totalCardWidth / 2)
  );
};

const getSubwayViewDefaultType = state => definitionSelectors.getDefaultSequenceType(state);

export default {
  getSubwayViewData,
  getSubwayViewSelectedData,
  getVisibleSubwayIdsForView,
  getAllowedThreadsForView,
  getIndexForXPosition,
  getDropPositionForIndex,
  getSubwayViewDefaultType
};
